// =============================================================================
// MeuDriver — StudentAchievements
// Página completa de conquistas do aluno — /aluno/conquistas
// =============================================================================

import { Trophy, Lock, Zap } from 'lucide-react'
import { motion } from 'framer-motion'
import { AchievementBadge } from '@/components/student/AchievementBadge'
import { XPProgressBar } from '@/components/student/XPProgressBar'
import { useAchievements } from '@/hooks/useAchievements'
import { Skeleton } from '@/components/ui/skeleton'

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  }),
}

export default function StudentAchievements() {
  const { data: achievements, isLoading } = useAchievements()

  const earned    = (achievements ?? []).filter((a) => a.earned)
  const notEarned = (achievements ?? []).filter((a) => !a.earned)
  const totalXP   = earned.reduce((sum, a) => sum + a.xp_reward, 0)

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
          <Trophy className="h-6 w-6 text-brand-600" />
          Minhas conquistas
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Complete desafios para ganhar XP e desbloquear conquistas.
        </p>
      </motion.div>

      {/* XP Progress */}
      <XPProgressBar />

      {/* Resumo */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Conquistadas', value: isLoading ? '—' : earned.length, color: 'text-brand-700', bg: 'bg-brand-50 border-brand-100' },
          { label: 'XP total',     value: isLoading ? '—' : totalXP.toLocaleString('pt-BR'), color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100' },
          { label: 'Disponíveis',  value: isLoading ? '—' : notEarned.length, color: 'text-gray-500', bg: 'bg-gray-50 border-gray-100' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`rounded-2xl border p-4 text-center ${bg}`}>
            <p className={`text-2xl font-extrabold ${color}`}>{value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Conquistas desbloqueadas */}
      {(isLoading || earned.length > 0) && (
        <section>
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-500" />
            Desbloqueadas ({isLoading ? '…' : earned.length})
          </h2>
          {isLoading ? (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-4">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
            </div>
          ) : (
            <motion.div
              variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-4 sm:grid-cols-5 gap-4"
            >
              {earned.map((a, i) => (
                <motion.div key={a.id} variants={fadeUp} custom={i}>
                  <AchievementBadge achievement={a} size="md" />
                </motion.div>
              ))}
            </motion.div>
          )}
        </section>
      )}

      {/* Conquistas bloqueadas */}
      {(isLoading || notEarned.length > 0) && (
        <section>
          <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Lock className="h-4 w-4 text-gray-400" />
            A desbloquear ({isLoading ? '…' : notEarned.length})
          </h2>
          {isLoading ? (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-4">
              {notEarned.map((a) => (
                <AchievementBadge key={a.id} achievement={a} size="md" />
              ))}
            </div>
          )}
        </section>
      )}

      {!isLoading && achievements?.length === 0 && (
        <p className="text-center text-muted-foreground py-10">Nenhuma conquista encontrada.</p>
      )}
    </div>
  )
}
